import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showSuccessUpdateAlert, showError } from '../../../../core/alert/sweetAlertConfig';
import { useNavigate } from 'react-router-dom';

interface RoomDTO {
  id: number;
  name: string;
  floorId: number;
  buildingId: number;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
  x?: number;
  y?: number;
  description?: string;
  doors?: any[];
  numberOfDoors?: number | null;
  numberOfWindows?: number | null;
}

interface UpdateRoomModuleProps {
  show: boolean;
  onHide: () => void;
  room: RoomDTO | null;
  onSuccess: () => void;
}

const NAME_MAX = 5;
const SCALE = 25; // 25 pixels = 1 meter, matching RoomRegister.tsx

const UpdateRoomModule: React.FC<UpdateRoomModuleProps> = ({
  show,
  onHide,
  room,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    status: '',
    floorId: '',
    buildingId: '',
    x: '',
    y: '',
    description: '',
    numberOfDoors: '',
    numberOfWindows: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const nameLen = formData.name.trim().length;
  const nameTooLong = nameLen > NAME_MAX;

  // Initialize formData when room prop changes, converting x and y from meters to pixels
  useEffect(() => {
    if (room) {
      setFormData({
        id: room.id.toString(),
        name: room.name ?? '',
        status: room.status ?? 'ACTIVE',
        floorId: room.floorId.toString(),
        buildingId: room.buildingId.toString(),
        x: room.x !== undefined ? (room.x ).toString() : '',
        y: room.y !== undefined ? (room.y ).toString() : '',

        description: room.description ?? '',
        numberOfDoors: room.numberOfDoors != null ? room.numberOfDoors.toString() : '',
        numberOfWindows: room.numberOfWindows != null ? room.numberOfWindows.toString() : '',
      });
    }
  }, [room]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === 'name') {
      setFormData((prev) => ({ ...prev, name: value.slice(0, NAME_MAX) }));
      return;
    }

    // Allow numbers or empty string for x, y, numberOfDoors, numberOfWindows
    if (['x', 'y', 'numberOfDoors', 'numberOfWindows'].includes(name)) {
      if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!room) {
      showError('No room data available. Please try again.');
      onHide();
      return;
    }

    // Basic validations
    if (!formData.name.trim()) {
      showError('Please enter a room name');
      return;
    }
    if (formData.name.trim().length > NAME_MAX) {
      showError('Name must be 5 characters or less.');
      return;
    }

    // Validate x, y, numberOfDoors, numberOfWindows
    const parsedX = formData.x === '' ? null : Number(formData.x);
    const parsedY = formData.y === '' ? null : Number(formData.y);
    const parsedDoors = formData.numberOfDoors === '' ? null : Number(formData.numberOfDoors);
    const parsedWindows = formData.numberOfWindows === '' ? null : Number(formData.numberOfWindows);

    if (parsedX !== null) {
      if (isNaN(parsedX) || parsedX < 0) {
        showError('X coordinate must be a non-negative number');
        return;
      }
      // Ensure x snaps to 25px grid (in pixels)
      if (parsedX % SCALE !== 0) {
        showError(`X coordinate must be a multiple of ${SCALE} pixels to align with the grid`);
        return;
      }
    }
    if (parsedY !== null) {
      if (isNaN(parsedY) || parsedY < 0) {
        showError('Y coordinate must be a non-negative number');
        return;
      }
      // Ensure y snaps to 25px grid (in pixels)
      if (parsedY % SCALE !== 0) {
        showError(`Y coordinate must be a multiple of ${SCALE} pixels to align with the grid`);
        return;
      }
    }
    if (parsedDoors !== null && (!Number.isInteger(parsedDoors) || parsedDoors < 0)) {
      showError('Number of doors must be a non-negative integer');
      return;
    }
    if (parsedWindows !== null && (!Number.isInteger(parsedWindows) || parsedWindows < 0)) {
      showError('Number of windows must be a non-negative integer');
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showError('Authentication token not found. Please log in.');
        navigate('/login');
        return;
      }

      const payload: any = {
        id: parseInt(formData.id, 10),
        name: formData.name.trim(),
        status: formData.status,
        floorId: parseInt(formData.floorId, 10),
        buildingId: parseInt(formData.buildingId, 10),
        description: formData.description || '',
        x: parsedX !== null ? (parsedX / SCALE).toFixed(3) : '0', // Convert pixels to meters
        y: parsedY !== null ? (parsedY / SCALE).toFixed(3) : '0', // Convert pixels to meters
        numberOfDoors: parsedDoors !== null ? parsedDoors.toString() : '0',
        numberOfWindows: parsedWindows !== null ? parsedWindows.toString() : '0',
      };

      const response = await apiDefinitions.updateRoom(payload);
      if (response.status === 200) {
        showSuccessUpdateAlert("Room updated successfully");
        onSuccess();
        onHide();
      } else {
        showError(response.data?.statusDescription || 'Failed to update room');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else if (err.response?.status === 400) {
        showError(err.response.data?.statusDescription || 'Invalid room data');
      } else if (err.response?.status === 409) {
        showError('Room name already exists on this floor');
      } else {
        showError('Failed to update room. Please try again.');
        console.error('updateRoom error:', err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!room) return null;

  const fmt = (d?: string) => (d ? new Date(d).toLocaleString() : '-');

  return (
    <Modal show={show} onHide={onHide} keyboard={!isLoading} centered size="lg">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Update Room: {room.name}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row className="g-3">
            {/* Left: Editable fields */}
            <Col md={4}>
              <h6 className="mb-2">Edit</h6>
              <Form.Group className="mb-3" controlId="roomName">
                <Form.Label>
                  Name <span className="text-muted">(max 5 characters)</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter room name"
                  required
                  maxLength={NAME_MAX}
                  isInvalid={nameTooLong}
                />
                <Form.Control.Feedback type="invalid">
                  Name must be 5 characters or less.
                </Form.Control.Feedback>
                <Form.Text className="text-muted">{nameLen}/{NAME_MAX}</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3" controlId="status">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  required
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                  <option value="ONGOING_REPAIR">ONGOING_REPAIR</option>
                </Form.Select>
              </Form.Group>
               <Form.Group className="mb-3" controlId="description">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter description (optional)"
                />
              </Form.Group>

            </Col>

            {/* Middle: Position and Counts */}
            <Col md={4}>
              <h6 className="mb-2">Counts</h6>

              <Form.Group className="mb-3" controlId="numberOfDoors">
                <Form.Label>Number of Doors</Form.Label>
                <Form.Control
                  type="text"
                  name="numberOfDoors"
                  value={formData.numberOfDoors}
                  onChange={handleInputChange}
                  placeholder="e.g., 2"
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="numberOfWindows">
                <Form.Label>Number of Windows</Form.Label>
                <Form.Control
                  type="text"
                  name="numberOfWindows"
                  value={formData.numberOfWindows}
                  onChange={handleInputChange}
                  placeholder="e.g., 2"
                />
              </Form.Group>
            </Col>

            {/* Right: Details */}
            <Col md={4}>
              <h6 className="mb-2">Details</h6>
              <Form.Group className="mb-3" controlId="roomId">
                <Form.Label>Room ID</Form.Label>
                <Form.Control type="text" value={formData.id} disabled />
              </Form.Group>

              <Form.Group className="mb-3" controlId="floorId">
                <Form.Label>Floor ID</Form.Label>
                <Form.Control type="text" value={formData.floorId} disabled />
              </Form.Group>

              <Form.Group className="mb-3" controlId="buildingId">
                <Form.Label>Building ID</Form.Label>
                <Form.Control type="text" value={formData.buildingId} disabled />
              </Form.Group>

            </Col>

            {/* Audit Info */}
            <Col md={12}>
              <h6 className="mb-2">Audit Info</h6>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3" controlId="createdBy">
                    <Form.Label>Created By</Form.Label>
                    <Form.Control type="text" value={room.createdBy || '-'} disabled />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3" controlId="updatedBy">
                    <Form.Label>Updated By</Form.Label>
                    <Form.Control type="text" value={room.updatedBy || '-'} disabled />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3" controlId="createdAt">
                    <Form.Label>Created At</Form.Label>
                    <Form.Control type="text" value={fmt(room.createdAt)} disabled />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3" controlId="updatedAt">
                    <Form.Label>Updated At</Form.Label>
                    <Form.Control type="text" value={fmt(room.updatedAt)} disabled />
                  </Form.Group>
                </Col>
              </Row>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-end">
          <Button variant="primary" onClick={onHide} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            type="submit"
            disabled={isLoading || !formData.name.trim() || !formData.status || nameTooLong}
          >
            {isLoading ? 'Updating...' : 'Update Room'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UpdateRoomModule;